import { useAuthInfo } from "@propelauth/react";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../ui/card";
import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchWithToken } from "../../api/apiBase";
import { QAModel } from "@/src/components/qa_forum/QAModel";
import { Button } from "../ui/button";
import { UserContext } from "../../contexts/UserContext";
import { ProjectContext } from "../../contexts/ProjectContext";
import QAItemForm from "./CreateQA";
import { QACard } from "./QAThread";
import EditQA from "./EditQA";
import { toast } from "../ui/use-toast";
import BaseLoadingSpinner from "../ui/baseLoadingSpinner";

type QAPageProps = {
    creating : boolean
}

/**
 *
 * todo - add the newly created data to the list on submit.
 * done
 * 
 * */
function QAPage({creating}: QAPageProps) {
    const navigate = useNavigate();
    const {accessToken} = useAuthInfo();
    const { project_id } = useParams<string>();
    const userContext = useContext(UserContext);
    const projectContext = useContext(ProjectContext);
    const [isCreatingNew, setisCreatingNew] = useState<boolean>(creating);
    const [editingQA, setEditingQA] = useState<QAModel | null>(null);

    const [data, setData] = useState<QAModel[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        if(accessToken) {
            try {
                setIsLoading(true);
                const response = await fetchWithToken(`/projects/${project_id}/qa/?skip=0&limit=10`, accessToken);
                setData(response);
                setError(null);
            } catch (err) {
                setError("Error loading QA :(");
            } finally {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchData()
    }, [project_id, accessToken]);

    if (isLoading) return <div><BaseLoadingSpinner/></div>;
    if (error)
        return (
            <div>Error loading QA :(</div>
        );

    const onCancelNew = () => {
        setisCreatingNew(false);
    };

    const handleEditQAModel = (qaItem: QAModel) => {
        setEditingQA(qaItem);
    };

    const handleCancelEdit = () => {
        setEditingQA(null);
    };

    const handleSaveEdit = (newQA: QAModel) => {
        setData((prev) => {    
            const existingIndex = prev.findIndex(item => item.id === newQA.id);
    
            if (existingIndex !== -1) {
                const updatedData = [...prev];
                updatedData[existingIndex] = newQA; 
                return updatedData;
            } else {
                return [newQA, ...prev];
            }
        });
        setEditingQA(null);
        setisCreatingNew(false);
    };

    return (
        <div className="space-y-6">
                <CardHeader>
                    <CardTitle>
                        <div className="flex justify-between items-center">
                            <h1 className="text-xm font-semibold">
                                Latest Questions and Answers
                            </h1>
                            {projectContext?.role === "builder" && (
                                <Button
                                    variant="outline"
                                    onClick={() => setisCreatingNew(true)}
                                >
                                    Ask a Question
                                </Button>
                            )}
                        </div>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">

                    {/* New item form */}
                    {isCreatingNew && (
                        <div>
                            <QAItemForm onCancel={onCancelNew} onSave={handleSaveEdit} />
                        </div>
                    )}

                    {/* Existing items */}
                    {data.map((qa: QAModel) =>
                        editingQA && editingQA.id === qa.id ? (
                            <EditQA
                                key={qa.id}
                                qa_id={qa.id}
                                onCancel={handleCancelEdit}
                                onSave={handleSaveEdit}
                            />
                        ) : (
                            <QACard
                                key={qa.id}
                                qa={qa}
                                project_id={project_id as string}
                                controls={{
                                    view: true,
                                    edit: userContext?.email === qa.created_by.email,
                                }}
                                user_company={userContext?.company_id as string}
                                onEdit={handleEditQAModel}
                            />
                        )
                    )}
                </CardContent>
        </div>
    );
}

export default QAPage;
